import React from 'react';
// @ts-ignore @TODO: add types for package
import Toggle from 'react-bootstrap-toggle';
import { connect } from 'react-redux';
import {
  LENDER_KEY_TO_LABEL,
  ORDERED_LENDERS,
  FinanceType,
  Condition,
} from '@upstart/harmony/lib/legos/constants';
import { FeatureConfig } from '@upstart/harmony/lib/legos/dealerTypes';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import { getCurrentDealerIncentivesConfig } from 'redux/dealers/selectors';
import { TieredAprConfig, TieredAprConfigOptions, TierKey } from '../types';

import { FINANCE_TYPE_TO_LABEL } from '../constants';
import { AprConfigValidationResult } from '../useAprConfigValidator';
import { PageMessage } from '../../../GlobalUI';

function TiersOptions({
  condition,
  aprConfig,
  onTierOptionsPartialUpdate,
  dealerIncentivesConfig,
}: {
  condition: Condition;
  aprConfig: TieredAprConfig;
  validationResult: AprConfigValidationResult;
  onTierOptionsPartialUpdate: (value: Partial<TieredAprConfigOptions>) => void;
  dealerIncentivesConfig: FeatureConfig['incentives'];
}) {
  return (
    <>
      <legend>Settings</legend>
      <div className="flex flex-col space-y-15">
        <div className="options flex flex-row space-x-30">
          <span className="options-label">Add OEM markup rate</span>
          <Toggle
            on="on"
            off="off"
            size="sm"
            className="toggle-switch"
            active={!!aprConfig.options.addOEMMarkupRate}
            onClick={() =>
              onTierOptionsPartialUpdate({
                addOEMMarkupRate: !aprConfig.options.addOEMMarkupRate,
              })
            }
          />
        </div>
      </div>
      <legend>Use Rates from AIS</legend>
      <div className="flex flex-col space-y-15">
        <div className="options flex flex-row space-x-10 items-start">
          {condition !== Condition.USED && (
            <div>
              <span className="bold">Use Manufacturer Rates (Purchase)</span>
              <Toggle
                on="on"
                off="off"
                size="sm"
                className="toggle-switch"
                active={!!aprConfig.options.enableManufacturerApr}
                onClick={() =>
                  onTierOptionsPartialUpdate({
                    enableManufacturerApr:
                      !aprConfig.options.enableManufacturerApr,
                  })
                }
              />
              {!!aprConfig.options.enableManufacturerApr &&
                aprConfig.tiers.purchase?.map((value, index) => {
                  const key = `tier${index + 1}` as TierKey;
                  return (
                    <div className="options flex flex-row space-x-30" key={key}>
                      <div className="checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={
                              !!aprConfig.options.useManufacturerApr?.[key]
                            }
                            onChange={() =>
                              onTierOptionsPartialUpdate({
                                ...aprConfig.options,
                                useManufacturerApr: {
                                  ...aprConfig.options.useManufacturerApr,
                                  [key]:
                                    !aprConfig.options.useManufacturerApr?.[
                                      key
                                    ],
                                },
                              })
                            }
                          />
                          <span>Override {`Tier ${index + 1}`} APR?</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
          <div>
            <span className="bold">
              {condition === Condition.NEW
                ? 'Use Third Party Lenders (Purchase & Lease)'
                : 'Use Third Party Lenders (Purchase)'}{' '}
            </span>
            <Toggle
              on="on"
              off="off"
              size="sm"
              className="toggle-switch"
              active={!!aprConfig.options.enableThirdPartyLenders}
              onClick={() => {
                if (
                  !aprConfig.options.enableThirdPartyLenders &&
                  dealerIncentivesConfig?.provider === 'ais' &&
                  dealerIncentivesConfig?.excludeIncentivesFromAffiliates
                ) {
                  PageMessage({
                    type: MessageType.ERROR,
                    message:
                      'The option "Exclude incentives from affiliates" (Set-up -> Configuration -> Incentives Settings) should be disabled in order to enable the third party lenders.',
                  });
                  return;
                }
                onTierOptionsPartialUpdate({
                  enableThirdPartyLenders:
                    !aprConfig.options.enableThirdPartyLenders,
                });
              }}
            />
            {aprConfig.options.enableThirdPartyLenders && (
              <div className="flex flex-col">
                {aprConfig.tiers.purchase?.map((_value, index) => {
                  const key = `tier${index + 1}` as TierKey;
                  return (
                    <div className="options flex flex-row space-x-10">
                      <div className="checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={
                              !!aprConfig.options
                                .overrideTierForThirdPartyLenders?.[key]
                            }
                            onChange={() =>
                              onTierOptionsPartialUpdate({
                                ...aprConfig.options,
                                overrideTierForThirdPartyLenders: {
                                  ...aprConfig.options
                                    .overrideTierForThirdPartyLenders,
                                  [key]:
                                    !aprConfig.options
                                      .overrideTierForThirdPartyLenders?.[key],
                                },
                              })
                            }
                          />
                          <span>Override {`Tier ${index + 1}`} APR?</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {aprConfig.options.enableThirdPartyLenders && (
            <div className="top-35">
              {ORDERED_LENDERS?.map((key) => (
                <div className="options flex flex-row space-x-30">
                  <div className="checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={!!aprConfig.options.thirdPartyLenders?.[key]}
                        onChange={() =>
                          onTierOptionsPartialUpdate({
                            ...aprConfig.options,
                            thirdPartyLenders: {
                              ...aprConfig.options.thirdPartyLenders,
                              [key]:
                                !aprConfig.options.thirdPartyLenders?.[key],
                            },
                          })
                        }
                      />
                      <span>{LENDER_KEY_TO_LABEL[key]}</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <legend>Use Manufacturer&apos;s Rebates?</legend>
      <div className="flex flex-col">
        <p>
          *Warning*: Turning off Online Purchase Rebates will turn off Buttons
          &gt;&gt; Incentives &amp; Rebates as well!
        </p>
        {[FinanceType.PURCHASE, FinanceType.LEASE, FinanceType.CASH].map(
          (financeType) => (
            <div className="options flex flex-row">
              <span className="bold">{FINANCE_TYPE_TO_LABEL[financeType]}</span>
              <Toggle
                on="on"
                off="off"
                size="sm"
                className="toggle-switch"
                active={
                  !!aprConfig.options?.useManufacturerRebate?.[financeType]
                }
                onClick={() =>
                  onTierOptionsPartialUpdate({
                    ...aprConfig.options,
                    useManufacturerRebate: {
                      ...aprConfig.options.useManufacturerRebate,
                      [financeType]:
                        !aprConfig?.options?.useManufacturerRebate?.[
                          financeType
                        ],
                    },
                  })
                }
              />
            </div>
          ),
        )}
      </div>
    </>
  );
}

const mapStateToProps = (state: object) => ({
  dealerIncentivesConfig: getCurrentDealerIncentivesConfig(state),
});

export default connect(mapStateToProps)(TiersOptions);
